// SCR-STAFF-871 (AP-INV-02؛ US-INV-004، 040، 041، 042): انبارها و زیرانبارها (Central، Branch، RoomCabinet، SalesShelf، Maintenance)، موجودی لحظه‌ای هر
// انبار (قابل مصرف، ظرف باز، در راه، منقضی، فراخوان‌شده و ارزش)، نقطه سفارش با نشان کمبود و پیشنهاد سفارش گرد به واحد خرید.
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type InvReorderRuleView,
  type InvReorderSuggestionView,
  type InvStockRowView,
  type InvWarehouseType,
  type InvWarehouseView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, DataTable, faDigits, FormDialog, ListPage, Money, SampleBadge, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { WriteNotice } from '../settings/common';
import { stockKeys, toMilli, useItemOptions, useQuantityText, useWarehouses, WAREHOUSE_TYPES, WarehouseSelect, warehouseName } from './stockCommon';

export const SCR_STAFF_871 = 'SCR-STAFF-871';

type TabId = 'stock' | 'warehouses' | 'reorder';

export function WarehousesStockPage() {
  const { t } = useTranslation('inv');
  const warehouses = useWarehouses();
  const [tab, setTab] = useState<TabId>('stock');
  const [warehouseId, setWarehouseId] = useState('');

  useEffect(() => {
    if (!warehouseId && warehouses.data?.items.length) {
      setWarehouseId((warehouses.data.items.find((warehouse) => warehouse.isDefault) ?? warehouses.data.items[0])!.id);
    }
  }, [warehouseId, warehouses.data]);

  return (
    <ListPage
      screenId={SCR_STAFF_871}
      titleKey="inv:stock.title"
      subtitleKey="inv:stock.subtitle"
      filters={
        <Stack spacing={1}>
          <Tabs value={tab} onChange={(_event, value: TabId) => setTab(value)} aria-label={t('inv:stock.title')}>
            <Tab value="stock" label={t('inv:stock.tabs.stock')} data-testid="stock-tab-stock" />
            <Tab value="warehouses" label={t('inv:stock.tabs.warehouses')} data-testid="stock-tab-warehouses" />
            <Tab value="reorder" label={t('inv:stock.tabs.reorder')} data-testid="stock-tab-reorder" />
          </Tabs>
          {tab !== 'warehouses' && <WarehouseSelect value={warehouseId} onChange={setWarehouseId} testId="stock-warehouse" />}
        </Stack>
      }
    >
      {tab === 'stock' && warehouseId && <StockTable warehouseId={warehouseId} />}
      {tab === 'warehouses' && <WarehousesTable />}
      {tab === 'reorder' && warehouseId && <ReorderPanel warehouseId={warehouseId} />}
    </ListPage>
  );
}

function StockTable({ warehouseId }: { warehouseId: string }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const quantity = useQuantityText();
  const [q, setQ] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const stock = useQuery({
    queryKey: [...stockKeys.stock(session?.tenantId, warehouseId), q, lowOnly],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/stock', { params: { query: { warehouseId, q: q.trim() || undefined, lowOnly: lowOnly || undefined, limit: 100 } } })),
  });

  const columns: DataTableColumn<InvStockRowView>[] = [
    {
      id: 'item',
      headerKey: 'inv:stock.columns.item',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.itemName}</span>
          {row.isSample && <SampleBadge />}
          {row.isLow && <Chip size="small" color="warning" label={t('inv:stock.low')} data-testid={`stock-low-${row.itemCode}`} />}
        </Stack>
      ),
    },
    { id: 'onHand', headerKey: 'inv:stock.columns.on_hand', cell: (row) => quantity(row.onHandMilli, row.baseUnit) },
    { id: 'available', headerKey: 'inv:stock.columns.available', cell: (row) => <span data-testid={`stock-available-${row.itemCode}`}>{quantity(row.availableMilli, row.baseUnit)}</span> },
    { id: 'open', headerKey: 'inv:stock.columns.open', cell: (row) => quantity(row.openMilli, row.baseUnit) },
    { id: 'transit', headerKey: 'inv:stock.columns.in_transit', cell: (row) => quantity(row.inTransitMilli, row.baseUnit) },
    { id: 'expired', headerKey: 'inv:stock.columns.expired', cell: (row) => quantity(row.expiredMilli, row.baseUnit) },
    { id: 'recalled', headerKey: 'inv:stock.columns.recalled', cell: (row) => quantity(row.recalledMilli, row.baseUnit) },
    { id: 'value', headerKey: 'inv:stock.columns.value', cell: (row) => <Money amountMinor={row.value.amount} currency={row.value.currency} /> },
    {
      id: 'batches',
      headerKey: 'inv:stock.columns.batches',
      cell: (row) =>
        row.batches.length === 0
          ? '—'
          : row.batches.map((batch) => `${batch.batchNumber ?? '—'} (${quantity(batch.quantityMilli, row.baseUnit)})`).join(t('common:list_separator')),
    },
  ];

  return (
    <DataTable
      captionKey="inv:stock.caption"
      columns={columns}
      rows={stock.data?.items}
      getRowId={(row) => `${row.itemId}-${row.warehouseId}`}
      isLoading={stock.isPending}
      error={stock.error}
      onRetry={() => void stock.refetch()}
      emptyTitleKey="inv:stock.empty"
      toolbar={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" label={t('inv:stock.search')} value={q} onChange={(event) => setQ(event.target.value)} sx={{ minWidth: 240 }} />
          <FormControlLabel control={<Switch checked={lowOnly} onChange={(event) => setLowOnly(event.target.checked)} />} label={t('inv:stock.low_only')} />
        </Stack>
      }
    />
  );
}

function WarehousesTable() {
  const { t } = useTranslation('inv');
  const warehouses = useWarehouses();
  const write = useWriteAccess('inv.warehouse.manage');
  const [creating, setCreating] = useState(false);

  const columns: DataTableColumn<InvWarehouseView>[] = [
    { id: 'name', headerKey: 'inv:warehouses.columns.name', cell: (row) => row.name },
    { id: 'code', headerKey: 'inv:warehouses.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'type',
      headerKey: 'inv:warehouses.columns.type',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <span>{t(`inv:warehouse_type.${row.type}`)}</span>
          {row.isDefault && <Chip size="small" label={t('inv:warehouses.default')} />}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'inv:warehouses.columns.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} labelKey={row.isActive ? 'inv:status.active' : 'inv:status.inactive'} />,
    },
  ];

  return (
    <Stack spacing={1}>
      <WriteNotice write={write} />
      <Stack direction="row">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="warehouse-create">
          {t('inv:warehouses.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="inv:warehouses.caption"
        columns={columns}
        rows={warehouses.data?.items}
        getRowId={(row) => row.id}
        isLoading={warehouses.isPending}
        error={warehouses.error}
        onRetry={() => void warehouses.refetch()}
        emptyTitleKey="inv:warehouses.empty"
      />
      {creating && <WarehouseDialog onClose={() => setCreating(false)} />}
    </Stack>
  );
}

function WarehouseDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const access = useAccess();
  const branchId = access.activeBranchId ?? access.branchIds[0] ?? '';
  const rooms = useQuery({
    queryKey: ['organization', 'rooms', session?.tenantId, 'inventory', branchId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/rooms', { params: { query: { branchId: branchId || undefined } } })),
  });
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<InvWarehouseType>('Central');
  const [roomId, setRoomId] = useState('');
  const [cashRegisterId, setCashRegisterId] = useState('');
  const [submitError, setSubmitError] = useState<unknown>();

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/warehouses', {
          body: { code: code.trim(), name: name.trim(), type, branchId, roomId: type === 'RoomCabinet' ? roomId || null : null, cashRegisterId: type === 'SalesShelf' ? cashRegisterId.trim() || null : null, responsibleMembershipId: null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stockKeys.warehouses(session?.tenantId) });
      onClose();
    },
    onError: (error) => setSubmitError(applyServerErrors(error, () => undefined, { fields: [] }) > 0 ? undefined : error),
  });

  return (
    <FormDialog open screenId="SCR-STAFF-871-WAREHOUSE-FORM" titleKey="inv:warehouses.create_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty={Boolean(code || name)} isSubmitting={save.isPending} submitError={submitError}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField required label={t('inv:warehouses.columns.code')} value={code} onChange={(event) => setCode(event.target.value)} fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'warehouse-code' } }} />
        <TextField required label={t('inv:warehouses.columns.name')} value={name} onChange={(event) => setName(event.target.value)} fullWidth slotProps={{ htmlInput: { 'data-testid': 'warehouse-name' } }} />
      </Stack>
      <TextField select required label={t('inv:warehouses.columns.type')} value={type} onChange={(event) => setType(event.target.value as InvWarehouseType)} slotProps={{ htmlInput: { 'data-testid': 'warehouse-type' } }}>
        {WAREHOUSE_TYPES.filter((value) => value !== 'Branch').map((value) => (
          <MenuItem key={value} value={value}>
            {t(`inv:warehouse_type.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {type === 'RoomCabinet' && (
        <TextField select required label={t('inv:warehouses.room')} value={roomId} onChange={(event) => setRoomId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'warehouse-room' } }}>
          {(rooms.data?.items ?? []).map((room) => (
            <MenuItem key={room.id} value={room.id}>
              {room.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {type === 'SalesShelf' && (
        <TextField required label={t('inv:warehouses.cash_register')} value={cashRegisterId} onChange={(event) => setCashRegisterId(event.target.value)} helperText={t('inv:warehouses.cash_register_help')} slotProps={{ htmlInput: { dir: 'ltr' } }} />
      )}
    </FormDialog>
  );
}

function ReorderPanel({ warehouseId }: { warehouseId: string }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const warehouses = useWarehouses();
  const write = useWriteAccess('inv.warehouse.manage');
  const [editing, setEditing] = useState(false);
  const rules = useQuery({
    queryKey: [...stockKeys.rules(session?.tenantId), warehouseId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/reorder-rules', { params: { query: { warehouseId } } })),
  });
  const suggestions = useQuery({
    queryKey: [...stockKeys.rules(session?.tenantId), warehouseId, 'suggestions'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/reorder-suggestions', { params: { query: { warehouseId } } })),
  });

  const ruleColumns: DataTableColumn<InvReorderRuleView>[] = [
    {
      id: 'item',
      headerKey: 'inv:stock.columns.item',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <span>{row.itemName}</span>
          {row.isLow && <Chip size="small" color="warning" label={t('inv:stock.low')} />}
        </Stack>
      ),
    },
    { id: 'min', headerKey: 'inv:reorder.columns.min', cell: (row) => faDigits(Number(row.minMilli) / 1000) },
    { id: 'max', headerKey: 'inv:reorder.columns.max', cell: (row) => faDigits(Number(row.maxMilli) / 1000) },
    { id: 'lead', headerKey: 'inv:reorder.columns.lead_time', cell: (row) => (row.leadTimeDays === null || row.leadTimeDays === undefined ? '—' : faDigits(Number(row.leadTimeDays))) },
  ];
  const suggestionColumns: DataTableColumn<InvReorderSuggestionView>[] = [
    { id: 'item', headerKey: 'inv:stock.columns.item', cell: (row) => row.itemName },
    { id: 'onHand', headerKey: 'inv:stock.columns.on_hand', cell: (row) => faDigits(Number(row.onHandMilli) / 1000) },
    { id: 'transit', headerKey: 'inv:stock.columns.in_transit', cell: (row) => faDigits(Number(row.inTransitMilli) / 1000) },
    { id: 'daily', headerKey: 'inv:reorder.columns.daily', cell: (row) => faDigits(Number(row.averageDailyMilli) / 1000) },
    {
      id: 'suggested',
      headerKey: 'inv:reorder.columns.suggested',
      cell: (row) => (
        <span data-testid={`suggestion-${row.itemId}`}>
          {faDigits(Number(row.suggestedPurchaseUnits))} {t(`inv:unit.${row.purchaseUnit}`)} ({faDigits(Number(row.suggestedMilli) / 1000)})
        </span>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(true)} disabled={!write.canWrite} data-testid="reorder-rule-create">
          {t('inv:reorder.add')}
        </Button>
        <span>{warehouseName(warehouses.data?.items, warehouseId)}</span>
      </Stack>
      <DataTable captionKey="inv:reorder.rules_caption" columns={ruleColumns} rows={rules.data?.items} getRowId={(row) => row.id} isLoading={rules.isPending} error={rules.error} emptyTitleKey="inv:reorder.empty" />
      <DataTable
        captionKey="inv:reorder.suggestions_caption"
        columns={suggestionColumns}
        rows={suggestions.data?.items}
        getRowId={(row) => row.itemId}
        isLoading={suggestions.isPending}
        error={suggestions.error}
        emptyTitleKey="inv:reorder.no_suggestions"
        emptyDescriptionKey="inv:reorder.purchase_note"
      />
      {editing && (
        <ReorderRuleDialog
          warehouseId={warehouseId}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void queryClient.invalidateQueries({ queryKey: stockKeys.rules(session?.tenantId) });
          }}
        />
      )}
    </Stack>
  );
}

function ReorderRuleDialog({ warehouseId, onClose, onSaved }: { warehouseId: string; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const items = useItemOptions();
  const [itemId, setItemId] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [lead, setLead] = useState('');
  const [submitError, setSubmitError] = useState<unknown>();
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/reorder-rules', {
          body: { itemId, warehouseId, minMilli: toMilli(min) ?? 0, maxMilli: toMilli(max) ?? 0, leadTimeDays: lead.trim() ? Number(lead) : null },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: onSaved,
    onError: setSubmitError,
  });
  return (
    <FormDialog open screenId="SCR-STAFF-871-REORDER-FORM" titleKey="inv:reorder.add" onSubmit={() => save.mutate()} onClose={onClose} isDirty={Boolean(itemId)} isSubmitting={save.isPending} submitError={submitError}>
      <TextField select required label={t('inv:stock.columns.item')} value={itemId} onChange={(event) => setItemId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'reorder-item' } }}>
        {(items.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField required label={t('inv:reorder.columns.min')} value={min} onChange={(event) => setMin(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': 'reorder-min' } }} />
        <TextField required label={t('inv:reorder.columns.max')} value={max} onChange={(event) => setMax(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': 'reorder-max' } }} />
        <TextField label={t('inv:reorder.columns.lead_time')} value={lead} onChange={(event) => setLead(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'reorder-lead' } }} />
      </Stack>
    </FormDialog>
  );
}
